/**
 * Currency Formatting Utilities
 * Handles currency symbol extraction and price formatting
 */

/**
 * Zero decimal currencies that should not show decimal places
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

/**
 * Options for price formatting
 */
export interface FormatPriceOptions {
  locale?: string;
  showSymbol?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Gets the currency symbol for a given ISO currency code
 * @param currencyCode - ISO currency code (e.g., 'USD', 'GBP', 'EUR')
 * @param locale - Optional locale (default: 'en-US')
 * @returns Currency symbol (e.g., '$', '£', '€')
 */
export const getCurrencySymbol = (currencyCode: string, locale: string = 'en-US'): string => {
  if (!currencyCode) {
    return ''; // No fallback - return empty if no currency code provided
  }

  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0); // Use 0 to isolate the symbol

    const currencyPart = parts.find((part) => part.type === 'currency');
    return currencyPart ? currencyPart.value : currencyCode;
  } catch (error) {
    console.warn(`Error getting currency symbol for ${currencyCode}:`, error);
    return currencyCode; // Fallback to currency code if symbol extraction fails
  }
};

/**
 * Checks if a currency is a zero decimal currency
 * @param currencyCode - ISO currency code
 * @returns True if the currency should not show decimals
 */
export const isZeroDecimalCurrency = (currencyCode: string): boolean => {
  return ZERO_DECIMAL_CURRENCIES.has(currencyCode.toUpperCase());
};

/**
 * Formats a price amount for display according to user view requirements
 * 
 * Uses dynamic decimal place detection via Intl.NumberFormat.resolvedOptions()
 * - 0 decimal currencies (JPY): ¥15,000,053 (input 15000053)
 * - 2 decimal currencies (USD): $150,000.53 (input 15000053)
 * - 3 decimal currencies (KWD): KWD 15,000.053 (input 15000053)
 * - INR uses 'en-IN' locale for lakhs/crores grouping: ₹1,50,000.53
 * 
 * @param amount - The price amount (integer representation in smallest unit)
 * @param currencyCode - ISO currency code (e.g., 'USD', 'GBP', 'EUR', 'INR', 'JPY', 'KWD')
 * @returns Formatted price string with symbol
 */
export const formatCurrencyForDisplay = (
  amount: number,
  currencyCode: string
): string => {
  // Use en-IN locale for INR to get lakhs/crores grouping (10,00,000 style)
  const locale = currencyCode.toUpperCase() === 'INR' ? 'en-IN' : 'en-US';
  const options: Intl.NumberFormatOptions = { style: 'currency', currency: currencyCode };

  try {
    // Get decimal places dynamically from Intl
    // This correctly handles: USD (2), JPY (0), KWD (3), etc.
    const fractionDigits = new Intl.NumberFormat(locale, options)
      .resolvedOptions().maximumFractionDigits ?? 2;

    // Convert integer to major unit based on currency's decimal places
    // USD: 15000053 / 10^2 = 150000.53
    // JPY: 15000053 / 10^0 = 15000053
    // KWD: 15000053 / 10^3 = 15000.053
    const displayAmount = amount / Math.pow(10, fractionDigits);

    return new Intl.NumberFormat(locale, options).format(displayAmount);
  } catch (error) {
    console.warn(`Error formatting currency for ${currencyCode}:`, error);
    // Fallback for invalid currency codes
    const symbol = getCurrencySymbol(currencyCode);
    const displayAmount = amount / 100; // Default to 2 decimals
    return `${symbol}${displayAmount.toFixed(2)}`;
  }
};


/**
 * Formats a price amount with currency symbol
 * @deprecated Use formatCurrencyForDisplay instead for consistent integer-based money handling
 * @param amount - The price amount
 * @param currencyCode - ISO currency code (e.g., 'USD', 'GBP', 'EUR')
 * @param options - Formatting options
 * @returns Formatted price string (e.g., '$20.00', '£20', '€20.00')
 */
export const formatPrice = (
  amount: number,
  currencyCode: string = 'USD',
  options: FormatPriceOptions = {}
): string => {
  const {
    locale = 'en-US',
    showSymbol = true,
    minimumFractionDigits,
    maximumFractionDigits,
  } = options;

  // Determine decimal places based on currency type
  const isZeroDecimal = isZeroDecimalCurrency(currencyCode);
  const minFractionDigits = minimumFractionDigits !== undefined
    ? minimumFractionDigits
    : isZeroDecimal
      ? 0
      : 2;
  const maxFractionDigits = maximumFractionDigits !== undefined
    ? maximumFractionDigits
    : isZeroDecimal
      ? 0
      : 2;

  try {
    if (showSymbol) {
      // Use Intl.NumberFormat for proper currency formatting
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: maxFractionDigits,
      }).format(amount);
    } else {
      // Format without currency symbol
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: maxFractionDigits,
      }).format(amount);
    }
  } catch (error) {
    console.warn(`Error formatting price for ${currencyCode}:`, error);
    // Fallback formatting
    const formattedAmount = isZeroDecimal
      ? Math.round(amount).toString()
      : amount.toFixed(2);
    const symbol = showSymbol ? getCurrencySymbol(currencyCode, locale) : '';
    return `${symbol}${formattedAmount}`;
  }
};

/**
 * Formats a price amount with currency symbol, but returns the symbol and amount separately
 * Useful for custom formatting in components
 * @param amount - The price amount
 * @param currencyCode - ISO currency code
 * @param locale - Optional locale (default: 'en-US')
 * @returns Object with symbol and formatted amount
 */
export const formatPriceParts = (
  amount: number,
  currencyCode: string = 'USD',
  locale: string = 'en-US'
): { symbol: string; amount: string } => {
  const isZeroDecimal = isZeroDecimalCurrency(currencyCode);
  const symbol = getCurrencySymbol(currencyCode, locale);

  try {
    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: isZeroDecimal ? 0 : 2,
      maximumFractionDigits: isZeroDecimal ? 0 : 2,
    });

    return {
      symbol,
      amount: formatter.format(amount),
    };
  } catch (error) {
    console.warn(`Error formatting price parts for ${currencyCode}:`, error);
    const formattedAmount = isZeroDecimal
      ? Math.round(amount).toString()
      : amount.toFixed(2);
    return {
      symbol,
      amount: formattedAmount,
    };
  }
};
